<template>
  <div class="info-card weather-card" v-loading="data.isLoading">
    <el-collapse-item :name="props.weatherId">
      <template #title>
        <span class="title-container">
          <span class="title-text">{{ props.topicTitle }}</span>
        </span>
      </template>

      <div v-if="!data.hourly.length && !data.isLoading" class="no-data flex-center">
        {{ data.errorMessage }}
      </div>

      <div class="weather-container" v-else>
        <el-row class="weather-content" v-if="data.currentConditions.Temperature">
          <el-col :xs="12" :span="6">
            <el-row align="middle">
              <i class="icon-Thermometer"></i>

              <span class="emphasized padded">
                {{ temperature }}
              </span>

              {{ temperatureUnit }}
            </el-row>

            <el-row align="middle" v-if="data.currentConditions.Wind">
              <i class="icon-Wind"></i>
              <span class="emphasized padded">{{ windSpeed }}</span>
              {{ windUnit }} {{ data.currentConditions.Wind.Direction.Localized }}
            </el-row>

            <el-row align="middle">
              <i class="icon-Sunglasses"></i>
              <span class="padded">
                {{ data.currentConditions.UVIndexText }}
              </span>
            </el-row>
          </el-col>

          <el-col :xs="12" :span="6" class="weather-content__today">
            <el-row justify="center">
              <i
                :class="`icon-${getWeatherIcon(data.currentConditions.WeatherIcon)} weather-icon`"
                :style="{ color: theme.accentColor }"
              ></i>
            </el-row>

            <el-row justify="center">
              <span class="weather-text">
                {{ data.currentConditions.WeatherText }}
              </span>
            </el-row>
          </el-col>

          <i class="icon-Reload" @click="getWeather(true)"></i>
        </el-row>

        <div class="hourly">
          <el-row class="hourly__inner">
            <el-col
              v-for="hour in data.hourly"
              :span="4"
              :sm="3"
              :key="hour.DateTime"
              class="column"
            >
              <span>{{ getHour(hour.DateTime) }}</span>
              <i
                class="hourly-icon"
                :class="`icon-${getWeatherIcon(hour.WeatherIcon)}`"
                :style="{ color: theme.accentColor }"
              ></i>
              <span>
                {{ convertTemperature(hour.RealFeelTemperature.Value) }}
                {{ temperatureUnit }}
              </span>

              <el-row justify="center" align="middle">
                <i class="icon-Water-Drop drop-icon"></i>
                {{ hour.PrecipitationProbability }}%
              </el-row>
            </el-col>
          </el-row>

          <div class="hourly__controls">
            <i class="icon-Chevron-Left" @click="scrollHourly('left')"></i>
            <i class="icon-Chevron-Right" @click="scrollHourly('right')"></i>
          </div>
        </div>

        <template v-if="data.isForecastIncluded">
          <el-row
            class="forecast-row"
            v-for="forecast in data.dailyForecasts"
            type="flex"
            :key="forecast.Date"
            :style="{ borderTop: `1px solid ${theme.accentColor}` }"
          >
            <el-col :span="4" class="flex-center">
              {{ getForecastDayName(forecast.Date) }}
            </el-col>

            <el-col :span="3" class="forecast-icon">
              <i
                :class="`icon-${getWeatherIcon(forecast.Day.Icon)}`"
                :style="{ color: theme.accentColor }"
              ></i>
            </el-col>

            <el-col :span="4" class="temperature-col">
              <div>
                <span>Hi</span>
                {{ convertTemperature(forecast.Temperature.Maximum.Value) }}
              </div>

              <div>
                <span>Lo</span>
                {{ convertTemperature(forecast.Temperature.Minimum.Value) }}
              </div>

              <span class="temp-unit">
                {{ temperatureUnit }}
              </span>
            </el-col>

            <el-col :span="4" class="wind-col">
              <div class="flex-center">
                <i class="icon-Wind"></i>
              </div>

              <div class="flex-center line-height--normal">
                {{ convertWindSpeed(forecast.Day.Wind.Speed.Value) }} {{ windUnit }}
              </div>

              <div class="flex-center line-height--small">
                {{ forecast.Day.Wind.Direction.Localized }}
              </div>
            </el-col>

            <el-col :span="4">
              <div class="flex-center">
                <i class="icon-Water-Drop drop-icon"></i>
              </div>

              <div class="flex-center">{{ forecast.Day.PrecipitationProbability }}%</div>
            </el-col>

            <el-col :span="6">
              <div class="flex-center">
                <i class="icon-Sunglasses"></i>
              </div>

              <div class="flex-center">{{ getUVIndex(forecast) }}</div>
            </el-col>
          </el-row>
        </template>

        <el-row
          type="flex"
          align="middle"
          class="settings"
          :style="{ borderTop: `1px solid ${theme.accentColor}` }"
        >
          <i class="icon-Thermometer"></i>

          <div class="switch">
            <div
              @click="setSwitchValue('isMetricTemp', true)"
              :class="{ active: data.isMetricTemp }"
              :style="{ backgroundColor: data.isMetricTemp ? theme.accentColor : 'inherit' }"
            >
              °C
            </div>

            <div
              @click="setSwitchValue('isMetricTemp', false)"
              :class="{ active: !data.isMetricTemp }"
              :style="{ backgroundColor: !data.isMetricTemp ? theme.accentColor : 'inherit' }"
            >
              °F
            </div>
          </div>

          <i class="icon-Wind"></i>

          <div class="switch">
            <div
              @click="setSwitchValue('isMetricWind', true)"
              :class="{ active: data.isMetricWind }"
              :style="{ backgroundColor: data.isMetricWind ? theme.accentColor : 'inherit' }"
            >
              kmh
            </div>

            <div
              @click="setSwitchValue('isMetricWind', false)"
              :class="{ active: !data.isMetricWind }"
              :style="{ backgroundColor: !data.isMetricWind ? theme.accentColor : 'inherit' }"
            >
              mph
            </div>
          </div>

          <a :href="accuWeatherLink" target="_blank">
            <img class="logo" src="@/assets/accuweather-logo.png" alt="AccuWeather" />
          </a>
        </el-row>
      </div>

      <el-row justify="end">
        <div
          class="info-card__btn"
          @click="onShareClick"
          :style="{ backgroundColor: theme.accentColor }"
        >
          <i class="icon-Share-3"></i>
        </div>
      </el-row>
    </el-collapse-item>
  </div>
</template>

<script lang="ts" setup>
import {
  CurrentConditions,
  DailyForecast,
  HourlyResponse,
  WeatherInterface
} from '@/interfaces/weather.interface'
import api from '@/api'
import iconMappings from './icon-mappings'
import { isMobile } from '@/utils/helpers'
import { computed, onMounted, reactive } from 'vue'
import { ElNotification } from 'element-plus'
import { useHelperStore } from '@/pinia/helper.store'
import { ShareTypesEnum } from '@/interfaces/guide.interface'

const helperStore = useHelperStore()
const emit = defineEmits<{
  (e: 'show-share-overlay', payload: { type: ShareTypesEnum; topicId: number }): void
}>()
const props = defineProps<{ weatherId: number; topicTitle: string }>()
const data = reactive({
  currentConditions: {} as CurrentConditions,
  hourly: [] as HourlyResponse[],
  isMetricTemp: false,
  isMetricWind: false,
  isForecastIncluded: false,
  isLoading: true,
  errorMessage: 'Sorry, no weather data available.',
  dailyForecasts: [] as DailyForecast[]
})

const theme = computed(() => helperStore.styleData)
const guideToken = computed(() => helperStore.guideToken)
const currentLanguage = computed(() => helperStore.currentLanguage)
const temperature = computed(() => {
  const type = data.isMetricTemp ? 'Metric' : 'Imperial'

  return data.currentConditions?.Temperature[type]?.Value.toFixed()
})
const temperatureUnit = computed(() => (data.isMetricTemp ? ' °C' : ' °F'))
const windSpeed = computed(() => {
  const type = data.isMetricWind ? 'Metric' : 'Imperial'

  return data.currentConditions?.Wind.Speed[type].Value.toFixed()
})
const windUnit = computed(() =>
  data.isMetricWind ? data.currentConditions?.Wind.Speed.Metric.Unit : 'mph'
)
const accuWeatherLink = computed(() =>
  isMobile() ? data.currentConditions.MobileLink : data.currentConditions.Link
)

onMounted(async () => {
  try {
    await getWeather()

    const preferences = localStorage.getItem('tsWeatherPreferences')

    if (!preferences) {
      return
    }

    const { isMetricTemp, isMetricWind } = JSON.parse(preferences)

    data.isMetricTemp = isMetricTemp
    data.isMetricWind = isMetricWind
  } catch (e) {
    console.error(e)
  }
})

async function getWeather(isManualRefresh = false) {
  const LS_KEY = 'tsWeatherLastFetched'
  const REQUEST_TIME_LIMIT = 60 * 60 * 1000 // 1 hour

  const lastTimeFetched = +localStorage.getItem(LS_KEY)
  const limitNotReached = Date.now() - lastTimeFetched <= REQUEST_TIME_LIMIT

  if (isManualRefresh && lastTimeFetched && limitNotReached) {
    ElNotification.info({
      title: helperStore.i18n('weather_refresh_title'),
      message: helperStore.i18n('weather_refresh_message'),
      duration: 5000
    })

    data.isLoading = false

    return
  }

  try {
    const response = (await api.getWeather(
      guideToken.value,
      currentLanguage.value
    )) as WeatherInterface

    data.currentConditions = response.current_conditions[0]
    data.hourly = response.hourly
    data.dailyForecasts = response.five_day.DailyForecasts
    data.isMetricTemp = response.isMetricTemp
    data.isMetricWind = response.isMetricWind
    data.isForecastIncluded = response.isForecastIncluded
    data.isLoading = false

    localStorage.setItem(LS_KEY, Date.now().toString())
  } catch (e) {
    console.error(e)
    data.isLoading = false

    if (e && e.includes && e.includes('timeout')) {
      data.errorMessage =
        "Oops, the weather isn't loading because the current internet connection is poor. " +
        'Please try again once you have a better connection.'
    }
  }
}

function scrollHourly(direction: 'left' | 'right') {
  const hourlyRef = document.querySelector('.hourly__inner')

  hourlyRef.scrollBy({
    left: direction === 'left' ? -327 : 327,
    behavior: 'smooth'
  })
}

function getUVIndex(forecast: DailyForecast): string {
  if (forecast.AirAndPollen) {
    return forecast.AirAndPollen[forecast.AirAndPollen.length - 1].Category
  }
}

function getWeatherIcon(iconNumber: number): string {
  const foundMapping = iconMappings.find((map) => map.numbers.includes(iconNumber))

  return foundMapping ? foundMapping.iconName : ''
}

function getHour(timestamp: string): string {
  return timestamp.split('T')[1].slice(0, 5)
}

function convertTemperature(temp: number): string {
  return data.isMetricTemp
    ? (((temp - 32) * 5) / 9).toFixed() // F to C
    : temp.toFixed()
}

function convertWindSpeed(speed: number): string {
  return data.isMetricWind
    ? (speed / 0.621371).toFixed() // mph to kmh
    : speed.toFixed()
}

function getForecastDayName(timestamp: string): string {
  const date = new Date(timestamp)

  return new Intl.DateTimeFormat(currentLanguage.value, { weekday: 'short' }).format(date)
}

function setSwitchValue(type: 'isMetricTemp' | 'isMetricWind', value: boolean): void {
  data[type] = value

  const preferences = { isMetricTemp: data.isMetricTemp, isMetricWind: data.isMetricWind }
  localStorage.setItem('tsWeatherPreferences', JSON.stringify(preferences))
}

function onShareClick() {
  emit('show-share-overlay', { type: ShareTypesEnum.TOPIC, topicId: props.weatherId })
}
</script>

<style lang="scss" scoped>
.no-data {
  padding: 15px;
  font-size: 0.875rem;
}

.weather-container {
  padding: 0 14px;
  font-size: 1rem;
  position: relative;

  .icon-Reload {
    position: absolute;
    right: 12px;
    font-size: 1rem;
    top: 0;
    cursor: pointer;
  }
}

i {
  cursor: default;
}

.weather-content {
  padding: 0 20px;

  span {
    line-height: 1.5;
  }

  &__today {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
  }

  .weather-text {
    text-align: center;
  }
}

.emphasized {
  font-size: 1.25rem;
}

.padded {
  padding: 0 5px 0 10px;
}

.settings {
  padding: 10px 0;

  .icon-Wind {
    margin: 0 10px;
  }

  .logo {
    width: 80px;
    position: absolute;
    right: 0;
  }
}

// was deep
.el-collapse-item__content {
  padding-bottom: 0;
}

.weather-icon:before {
  font-size: 52px;
}

.switch {
  border-radius: $border-radius * 2;
  background-color: #c4c4c4;
  border: 0.5px solid #6d6d6d;
  font-size: 0.875rem;
  overflow: hidden;
  display: flex;

  div {
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    padding: 0 $padding;
    width: 50%;
    transition: all 0.3s ease-in-out;

    &.active {
      color: white;
    }

    &:first-child {
      border-right: 1px solid #6d6d6d;
    }
  }
}

.hourly {
  overflow: hidden;
  padding: 20px 0;
  font-size: 0.875rem;
  position: relative;

  &__inner {
    overflow: auto;
    flex-wrap: nowrap;
    scrollbar-width: none; /* Firefox */

    .el-col {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__controls {
    i {
      position: absolute;
      cursor: pointer;
      top: 50%;
      transform: translateY(-50%);
    }

    .icon-Chevron-Left {
      left: -7px;
    }

    .icon-Chevron-Right {
      right: -7px;
    }
  }

  &-icon {
    padding: 5px 0;
  }

  .drop-icon {
    margin-right: 3px;
    font-size: 0.75rem;
    transform: rotate(45deg);
  }
}

.forecast-row {
  padding: 10px 0;
  font-size: 0.75rem;

  i {
    font-size: 1rem;
  }

  .forecast-icon {
    display: flex;
    align-items: center;

    i {
      font-size: 2rem;
    }
  }

  .drop-icon {
    transform: rotate(45deg);
  }

  .flex-center {
    flex: 1 0 auto;
  }
}

.flex-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.temperature-col {
  position: relative;
  flex-direction: column;
  display: flex;
  justify-content: center;

  .temp-unit {
    position: absolute;
    left: 30px;
    top: 50%;
    line-height: 1;
    transform: translateY(-50%);
  }
}

.wind-col {
  .line-height--normal {
    line-height: 1.125rem;
  }
  .line-height--small {
    line-height: 0.75rem;
  }
}

.title-text {
  color: inherit;
  font-family: v-bind('helperStore.headerFont');
}
</style>
